import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import key from 'weak-key';
import type { AnyAction } from 'redux';

// components
import { ProductDeprecatedPlaceholder } from './product-deprecated-placeholder';
import { SingleTile } from 'components/Product/Tile/SingleTile';
import { TilesView } from 'components/Product/Tile/TilesView';

// utils
import { fetchProducts } from 'components/Product/actions';
import {
  productsByProductKeysSelector,
  isSectionLoadedBySectionIdSelector,
  currentCatalogSelector,
} from 'utils/selectors/productCatalogSelectors';
import { hashCode } from 'utils/hash';
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { isEmpty } from 'utils/is-empty';

export type ProductTiles = {
  productItems: string[];
  isWebshop: boolean;
  previewId: string;
};

export function ProductTiles({
  productItems,
  previewId,
  isWebshop = false,
  ...rest
}: Readonly<ProductTiles>) {
  const isPreview = useIsPreview();
  const available = useSelector((state: AppState) => productsByProductKeysSelector(state, productItems));
  const isFetching = useSelector((state: AppState) =>
    isSectionLoadedBySectionIdSelector(state, hashCode(previewId)),
  );
  const catalogLanguage = useSelector(currentCatalogSelector);
  const dispatch = useDispatch();

  let products = available;
  if (isPreview) {
    if (isFetching === false) {
      products = productItems.map(
        (p) =>
          available.find((a) => a.key === p) || {
            key: p,
            deprecated: true,
          },
      );
    } else {
      products = [];
    }
  }

  useEffect(() => {
    if (!isEmpty(productItems)) {
      dispatch(fetchProducts(productItems, previewId, catalogLanguage ?? '') as unknown as AnyAction);
    }
  }, [catalogLanguage, dispatch, previewId, productItems]);

  const renderTiles = () =>
    products.map((product) => (
      <div className="c-product-tiles__column" key={key(product)}>
        {isPreview && product.deprecated && <ProductDeprecatedPlaceholder product={product.key} />}
        <SingleTile product={product} linkType="product_detail_page" isWebshop={isWebshop} />
      </div>
    ));

  return (
    <TilesView renderTiles={renderTiles} componentName="product-tiles" items={products} {...rest} />
  );
}
